import React, { useEffect, useState } from "react";
import { CgProfile } from "react-icons/cg";
import { FaBuilding, FaEnvelope, FaMobileAlt, FaFileInvoice } from "react-icons/fa";
import { getMySubscription } from "../services/SubscriptionService";
import {
    formatINR,
    formatDate,
    formatBillingCycle,
    statusMeta,
    downloadInvoicePdf,
    canViewSubscriptionBilling,
} from "./Subscription/subscriptionUtils";

const getUserData = () => {
    try {
        return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
        return {};
    }
};

const roleLabel = (role, masterAccount) => {
    if (role === 1) return "Super Admin";
    if (masterAccount) return "Master Account";
    return "Branch User";
};

const Profile = () => {
    const userData = getUserData();
    const showSubscription = canViewSubscriptionBilling(userData);
    const [subData, setSubData] = useState(null);
    const [loadingSub, setLoadingSub] = useState(showSubscription);

    useEffect(() => {
        if (!showSubscription) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await getMySubscription();
                if (!cancelled && res?.success) setSubData(res.data);
            } catch (e) {
                // non-blocking
            } finally {
                if (!cancelled) setLoadingSub(false);
            }
        })();
        return () => { cancelled = true; };
    }, [showSubscription]);

    const sub = subData?.subscription;
    const status = subData?.status;
    const state = status?.state || "none";
    const meta = statusMeta(state);
    const paidInvoices = (subData?.invoices || []).filter((i) => i.status === "paid");

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Page header */}
                <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-md">
                        {(userData.name || "U").charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{userData.name || "My Profile"}</h1>
                        <p className="text-sm text-gray-500">{userData.branchName || userData.email || "Account overview"}</p>
                    </div>
                </div>

                {/* Account details */}
                <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/80">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <CgProfile className="text-blue-600" /> Account Details
                        </h2>
                    </div>
                    <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InfoRow icon={<CgProfile />} label="Full Name" value={userData.name} />
                        <InfoRow icon={<FaEnvelope />} label="Email" value={userData.email} />
                        <InfoRow icon={<FaMobileAlt />} label="Mobile" value={userData.mobile} />
                        <InfoRow icon={<FaBuilding />} label="Branch" value={userData.branchName} />
                        <InfoRow label="Role" value={roleLabel(userData.role, userData.masterAccount)} />
                        {userData.businessCategory && (
                            <InfoRow label="Business Category" value={userData.businessCategory} />
                        )}
                        {(userData.gstin || userData.gstNumber) && (
                            <InfoRow label="GSTIN" value={userData.gstin || userData.gstNumber} />
                        )}
                    </div>
                </section>

                {/* Subscription */}
                {showSubscription && (
                    <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/80 flex flex-wrap items-center justify-between gap-3">
                            <h2 className="text-lg font-semibold text-gray-900">Subscription & Billing</h2>
                            {!loadingSub && sub && (
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${meta.bg} ${meta.text}`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                                    {meta.label}
                                </span>
                            )}
                        </div>

                        {loadingSub ? (
                            <div className="flex justify-center py-16">
                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
                            </div>
                        ) : !sub ? (
                            <div className="p-8 text-center text-gray-500">
                                <p className="font-medium text-gray-700">No subscription configured yet</p>
                                <p className="text-sm mt-1">Contact your service provider to set up your plan.</p>
                            </div>
                        ) : (
                            <div className="p-6 space-y-6">
                                {subData?.pendingPayment && (
                                    <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
                                        Payment submitted on {formatDate(subData.pendingPayment.metaData?.createdAt)} — verification in progress.
                                        Your portal stays active while we review your payment.
                                    </div>
                                )}

                                {state === "expiring" && (
                                    <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                                        Your subscription expires in <strong>{status.daysRemaining} day{status.daysRemaining === 1 ? "" : "s"}</strong> ({formatDate(sub.endDate)}).
                                        Renew early from the renewal prompt to avoid interruption.
                                    </div>
                                )}

                                {state === "grace" && (
                                    <div className="rounded-xl bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-orange-800">
                                        Subscription expired {status.daysOverdue} day{status.daysOverdue === 1 ? "" : "s"} ago.
                                        You are in the grace period — renew soon to restore full access.
                                    </div>
                                )}

                                {state === "expired" && !subData?.pendingPayment && (
                                    <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-800">
                                        Your subscription has expired. Renew to restore portal access.
                                    </div>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <StatCard label="Plan" value={sub.planName || "—"} />
                                    <StatCard label="Billing Cycle" value={formatBillingCycle(sub.billingCycle)} />
                                    <StatCard
                                        label="Amount / Cycle"
                                        value={formatINR(sub.subtotal)}
                                        highlight
                                        sublines={
                                            sub.gstEnabled
                                                ? [
                                                    {
                                                        text: `+ ${formatINR(sub.gstAmount)} GST (${sub.gstRate || 0}%)`,
                                                        className: "text-[10px] text-gray-400 font-normal",
                                                    },
                                                ]
                                                : undefined
                                        }
                                    />
                                    <StatCard label="Start Date" value={formatDate(sub.startDate)} />
                                    <StatCard label="Expiry Date" value={formatDate(sub.endDate)} highlight={state === "expiring" || state === "expired"} />
                                    <StatCard
                                        label={state === "expired" || state === "grace" ? "Days Overdue" : "Days Remaining"}
                                        value={
                                            state === "expired" || state === "grace"
                                                ? (status.daysOverdue != null ? `${status.daysOverdue} days` : "—")
                                                : (status.daysRemaining != null ? `${status.daysRemaining} days` : "—")
                                        }
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                                    <InfoRow label="Base Price" value={formatINR(sub.basePrice)} />
                                    {sub.gstEnabled && (
                                        <>
                                            <InfoRow label={`GST (${sub.gstRate || 0}%)`} value={formatINR(sub.gstAmount)} />
                                            <InfoRow label="Subtotal (before GST)" value={formatINR(sub.subtotal)} />
                                        </>
                                    )}
                                    <InfoRow label="Grace Period" value={`${sub.gracePeriodDays || 0} days after expiry`} />
                                    <InfoRow label="Renewal Reminder" value={`${sub.reminderDays ?? 7} days before expiry`} />
                                    <InfoRow label="Currency" value={sub.currency || "INR"} />
                                </div>

                                {(sub.features || []).filter((f) => f.enabled).length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Included Features</p>
                                        <div className="flex flex-wrap gap-2">
                                            {sub.features.filter((f) => f.enabled).map((f, i) => (
                                                <span key={i} className="px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-800 text-sm border border-indigo-100">
                                                    {f.name} · {formatINR(f.price)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {paidInvoices.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                                            <FaFileInvoice /> Paid Invoices
                                        </p>
                                        <div className="space-y-2">
                                            {paidInvoices.slice(0, 8).map((inv) => (
                                                <div
                                                    key={inv._id}
                                                    className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-50"
                                                >
                                                    <div>
                                                        <p className="font-medium text-gray-800">{inv.invoiceNumber}</p>
                                                        <p className="text-xs text-gray-500">
                                                            Issued {formatDate(inv.issueDate)} · {formatINR(inv.total)}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                                                            Paid
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => downloadInvoicePdf(inv)}
                                                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                                        >
                                                            Download PDF
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {subData?.settings && (subData.settings.supportEmail || subData.settings.supportPhone) && (
                                    <div className="text-sm text-gray-500 pt-2 border-t border-gray-100">
                                        Billing support:{" "}
                                        {[subData.settings.supportEmail, subData.settings.supportPhone].filter(Boolean).join(" · ")}
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                )}

            </div>
        </div>
    );
};

const InfoRow = ({ icon, label, value }) => (
    <div className="flex items-start gap-3">
        {icon && <span className="text-gray-400 mt-0.5 shrink-0">{icon}</span>}
        <div className="min-w-0">
            <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
            <p className="text-sm font-medium text-gray-900 mt-0.5 break-words">{value || "—"}</p>
        </div>
    </div>
);

const StatCard = ({ label, value, highlight, sublines }) => (
    <div className={`rounded-xl border p-4 ${highlight ? "border-blue-200 bg-blue-50/50" : "border-gray-200 bg-gray-50/50"}`}>
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
        <p className={`text-base font-semibold mt-1 ${highlight ? "text-blue-900" : "text-gray-900"}`}>{value || "—"}</p>
        {sublines?.length > 0 && (
            <div className="mt-1.5 space-y-0.5">
                {sublines.map((line, i) => {
                    const text = typeof line === "string" ? line : line.text;
                    const className = typeof line === "string"
                        ? "text-xs text-gray-400 font-normal"
                        : (line.className || "text-xs text-gray-400 font-normal");
                    return <p key={i} className={className}>{text}</p>;
                })}
            </div>
        )}
    </div>
);

export default Profile;
